// In-app Olene sign-in (olene-platform/docs/signin.md).
// Steps: sign in -> (choose a new password | authenticator code) ; forgot password A -> B.
// Every call goes browser -> Cognito (SRP). Nothing here talks to the PayClear backend.
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import {
  completeNewPassword, confirmNewPassword, forgotPassword, sendTotp, signIn,
} from "../auth/olene";
import { forgotUsername } from "../auth/forgotUsername";
import { linkAccount, retrySsoOnReturn, trySso } from "../auth/oleneSso";
import { HUB } from "./Sso";
import { APP_NAME, MSG, messageFor, normalizeIdentifier, resetSentFor, signInMessageFor } from "../auth/messages";
import {
  Alert, Field, Notice, OleneAuthCard, OleneBubble, PasswordField, PrimaryButton, TextLink,
} from "../components/OleneAuthCard";

export default function SignIn() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState("signIn"); // signIn | newPassword | totp | forgotA | forgotB | forgotUser
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(location.state?.notice || "");
  const [showActivate, setShowActivate] = useState(false);
  const challengeUser = useRef(null);
  const firstField = useRef(null);

  // Signed out here: maybe signed in at account.olenesoft.com (another Olene app). Asks once per tab.
  const [redirecting] = useState(() => trySso("payclear", HUB));
  // Back on this tab after signing in to another Olene app: pick that session up.
  useEffect(() => (step === "signIn" ? retrySsoOnReturn("payclear", HUB) : undefined), [step]);

  // Focus the first field of each step.
  useEffect(() => {
    firstField.current?.focus();
  }, [step]);

  const go = (next, { keepNotice = false } = {}) => {
    setError("");
    if (!keepNotice) setNotice("");
    setShowActivate(false);
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    setStep(next);
  };

  const finish = (session) => {
    auth.setSession(session);
    // Take the session to account.olenesoft.com so the other Olene apps can use it, then come back.
    const dest = location.pathname === "/signin" ? "/" : location.pathname + location.search;
    if (linkAccount("payclear", HUB, dest)) return;
    if (location.pathname === "/signin") navigate("/", { replace: true });
  };

  const handleResult = (result) => {
    if (result.status === "signedIn") return finish(result.session);
    challengeUser.current = result.user;
    if (result.status === "newPasswordRequired") go("newPassword");
    else if (result.status === "totpRequired") go("totp");
  };

  // Wraps a submit handler: busy flag, error mapping.
  const run = (fn) => async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  const onSignIn = run(async () => {
    const username = normalizeIdentifier(identifier);
    setNotice("");
    setShowActivate(false);
    try {
      handleResult(await signIn(username, password));
    } catch (err) {
      const name = err?.code || err?.name;
      if (name === "PasswordResetRequiredException") {
        try { await forgotPassword(username); } catch {}
        setIdentifier(username);
        setPassword("");
        go("forgotB");
        setNotice(MSG.resetRequired);
        return;
      }
      if (name === "UserNotConfirmedException") setShowActivate(true);
      if (name === "NotAuthorizedException" || name === "UserNotFoundException") {
        setError(signInMessageFor(err, username));
        return;
      }
      throw err;
    }
  });

  const onNewPassword = run(async () => {
    if (newPassword !== confirmPassword) {
      setError(MSG.mismatch);
      return;
    }
    handleResult(await completeNewPassword(challengeUser.current, newPassword));
  });

  const onTotp = run(async () => {
    handleResult(await sendTotp(challengeUser.current, code.trim()));
  });

  const onForgotA = run(async () => {
    const username = normalizeIdentifier(identifier);
    setIdentifier(username);
    try {
      await forgotPassword(username);
    } catch (err) {
      const name = err?.code || err?.name;
      // Rate limits are safe to report; anything else would reveal whether the account exists.
      if (["LimitExceededException", "TooManyRequestsException", "TooManyFailedAttemptsException"].includes(name)) throw err;
    }
    go("forgotB");
    setNotice(resetSentFor(username));
  });

  // Forgot username: the account service emails it (auth/forgotUsername.js).
  const onForgotUser = run(async () => {
    const email = identifier.trim().toLowerCase();
    if (!email.includes("@")) {
      setError("Enter the email address on your account.");
      return;
    }
    await forgotUsername(email);
    setIdentifier("");
    go("signIn");
    setNotice(MSG.usernameSent);
  });

  const onForgotB = run(async () => {
    if (newPassword !== confirmPassword) {
      setError(MSG.mismatch);
      return;
    }
    const username = normalizeIdentifier(identifier);
    await confirmNewPassword(username, code.trim(), newPassword);
    handleResult(await signIn(username, newPassword));
  });

  if (redirecting) {
    return (
      <OleneAuthCard title={`Sign in to ${APP_NAME}`}>
        <p role="status" className="text-sm text-gray-600">Checking your Olene account…</p>
      </OleneAuthCard>
    );
  }

  if (step === "newPassword") {
    return (
      <OleneAuthCard title="Choose a new password">
        <p className="text-sm text-gray-600 mb-5">For your security, set a new password to finish signing in.</p>
        <form onSubmit={onNewPassword} noValidate>
          <Alert>{error}</Alert>
          <PasswordField id="new-password" label="New password" autoComplete="new-password" required
            value={newPassword} onChange={(e) => setNewPassword(e.target.value)} inputRef={firstField}
            hint={MSG.passwordRules} />
          <PasswordField id="confirm-password" label="Confirm new password" autoComplete="new-password" required
            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          <PrimaryButton busy={busy} busyLabel="Saving…">Save and sign in</PrimaryButton>
        </form>
      </OleneAuthCard>
    );
  }

  if (step === "totp") {
    return (
      <OleneAuthCard title="Enter your authentication code">
        <p className="text-sm text-gray-600 mb-5">Open your authenticator app and enter the 6-digit code for your Olene account.</p>
        <form onSubmit={onTotp} noValidate>
          <Alert>{error}</Alert>
          <Field id="totp" label="Authentication code" inputMode="numeric" autoComplete="one-time-code"
            pattern="[0-9]*" maxLength={6} required value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} inputRef={firstField} />
          <PrimaryButton busy={busy} busyLabel="Verifying…">Verify</PrimaryButton>
        </form>
      </OleneAuthCard>
    );
  }

  if (step === "forgotA") {
    return (
      <OleneAuthCard title="Reset your password">
        <form onSubmit={onForgotA} noValidate>
          <Alert>{error}</Alert>
          <Field id="identifier" label="Email or username" autoComplete="username" autoCapitalize="none"
            spellCheck={false} required value={identifier}
            onChange={(e) => setIdentifier(e.target.value)} inputRef={firstField} />
          <PrimaryButton busy={busy} busyLabel="Sending…">Send code</PrimaryButton>
        </form>
        <div className="mt-4 text-center">
          <TextLink onClick={() => go("signIn")}>Back to sign in</TextLink>
        </div>
      </OleneAuthCard>
    );
  }

  if (step === "forgotUser") {
    return (
      <OleneAuthCard title="Find your username">
        <form onSubmit={onForgotUser} noValidate>
          <p className="text-sm text-gray-600 mb-4">Enter the email on your account. We'll email you your username.</p>
          <Alert>{error}</Alert>
          <Field id="email" label="Email" type="email" autoComplete="email" autoCapitalize="none"
            spellCheck={false} required value={identifier}
            onChange={(e) => setIdentifier(e.target.value)} inputRef={firstField} />
          <PrimaryButton busy={busy} busyLabel="Sending…">Email my username</PrimaryButton>
        </form>
        <div className="mt-4 text-center">
          <TextLink onClick={() => go("signIn")}>Back to sign in</TextLink>
        </div>
      </OleneAuthCard>
    );
  }

  if (step === "forgotB") {
    return (
      <OleneAuthCard title="Reset your password">
        <form onSubmit={onForgotB} noValidate>
          <Notice>{notice}</Notice>
          <Alert>{error}</Alert>
          {/* Lets password managers pair the new password with the account. */}
          <input type="hidden" autoComplete="username" value={identifier} readOnly />
          <Field id="code" label="Code" inputMode="numeric" autoComplete="one-time-code" required
            value={code} onChange={(e) => setCode(e.target.value.trim())} inputRef={firstField} />
          <PasswordField id="new-password" label="New password" autoComplete="new-password" required
            value={newPassword} onChange={(e) => setNewPassword(e.target.value)} hint={MSG.passwordRules} />
          <PasswordField id="confirm-password" label="Confirm new password" autoComplete="new-password" required
            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          <PrimaryButton busy={busy} busyLabel="Resetting…">Reset password</PrimaryButton>
        </form>
        <div className="mt-4 text-center">
          <TextLink onClick={() => go("signIn")}>Back to sign in</TextLink>
        </div>
      </OleneAuthCard>
    );
  }

  return (
    <OleneAuthCard
      title={`Sign in to ${APP_NAME}`}
      footer={
        <p className="text-center text-sm text-gray-600 mt-5">
          New to {APP_NAME}?{" "}
          <a href="/signup" className="font-semibold text-green-700 hover:text-green-800 hover:underline">Create account</a>
        </p>
      }
    >
      <OleneBubble />
      <form onSubmit={onSignIn} noValidate>
        <Notice>{notice}</Notice>
        <Alert>
          {error && (
            <>
              {error}
              {showActivate && (
                <>
                  {" "}
                  <a href={`/signup?confirm=${encodeURIComponent(normalizeIdentifier(identifier))}`}
                    className="font-semibold underline">Enter your activation code</a>
                </>
              )}
            </>
          )}
        </Alert>
        <Field id="identifier" label="Email or username" autoComplete="username" autoCapitalize="none"
          spellCheck={false} required value={identifier}
          onChange={(e) => setIdentifier(e.target.value)} inputRef={firstField} />
        <PasswordField id="password" label="Password" autoComplete="current-password" required
          value={password} onChange={(e) => setPassword(e.target.value)} />
        <div className="flex justify-between -mt-2 mb-5">
          <TextLink onClick={() => go("forgotUser")}>Forgot username?</TextLink>
          <TextLink onClick={() => go("forgotA")}>Forgot password?</TextLink>
        </div>
        <PrimaryButton busy={busy} busyLabel="Signing in…">Sign in</PrimaryButton>
      </form>
    </OleneAuthCard>
  );
}

// Step 5: signed in to Olene, but the PayClear backend refuses the account.
export function NoAccess() {
  const auth = useAuth();
  return (
    <OleneAuthCard title={`Sign in to ${APP_NAME}`}>
      <div role="alert" className="text-sm text-gray-700 mb-5">{MSG.noAccess}</div>
      <TextLink onClick={() => auth.signOut()}>Sign out</TextLink>
    </OleneAuthCard>
  );
}
